// Returns a user to the page they logged in from after the /select-org diversion — for multi-org
// picks as well as the zero- and single-org flows that skip the picker and auto-redirect. The
// originating page is parked in sessionStorage (origin-scoped, survives the Auth Provider hop):
// callback writes it, /select-org consumes it, "Switch org" clears it. Storage access is wrapped so
// a quota/security error degrades to "no drop-off" — same posture as orgContextStore.

import { Routes } from '~/modules/routes';
import { orgContextStore } from '~/modules/stores/orgContextStore';
import { navigate } from '~/modules/stores/routerStore';
import { handleException } from '~/modules/utilities/error_utilities';
import { logger } from '~/modules/utilities/logger';

const ORG_SWITCH_DROP_OFF_KEY = 'orgSwitchDropOff';

// Root is the default return; never worth persisting, treated as absent on read.
const SITE_ROOT_PATH = '/';

// Percent-encoded path separators: "/%2F%2Fevil" is a valid same-origin path to the WHATWG parser
// (it keeps %2F literal), but callback hands this value to the server-side Auth Provider as
// ?startURL=. A server that decodes it once more than expected would turn "/%2F%2Fevil" into
// "///evil" — cross-origin. Scope this check to the pathname so encoded separators remain valid in
// query values such as search terms.
const ENCODED_UNSAFE_PATH_SEPARATOR = /%(?:2f|5c)/i;

// Encoded C0 control chars and DEL (%00-%1f, %7f) are rejected across the full value because a
// downstream decode could reintroduce header-splitting or characters the WHATWG parser drops.
const ENCODED_UNSAFE_CONTROL = /%(?:0[0-9a-f]|1[0-9a-f]|7f)/i;

// Same-origin relative paths only. Beyond "//host" and "https://…", browsers fold "\" to "/" and
// strip C0 control chars (tab/newline/CR) during URL parsing, so "/\evil" or "/\tx" would slip past
// startsWith('//') and resolve cross-origin — reject backslashes and control chars too. Open-redirect guard.
export function isSafeRelativePath(path: string | null | undefined): path is string {
    if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('//')) {
        return false;
    }
    const queryOrFragmentIndex = path.search(/[?#]/);
    const pathname = queryOrFragmentIndex === -1 ? path : path.slice(0, queryOrFragmentIndex);
    if (ENCODED_UNSAFE_PATH_SEPARATOR.test(pathname) || ENCODED_UNSAFE_CONTROL.test(path)) {
        return false;
    }
    for (let i = 0; i < path.length; i++) {
        const code = path.charCodeAt(i);
        if (path[i] === '\\' || code <= 0x1f || code === 0x7f) {
            return false;
        }
    }
    return true;
}

// Stores a safe non-root page; root or unsafe clears the key, so each login owns the drop-off and a
// prior login's value can't linger.
export function persistOrgSwitchDropOff(path: string | null | undefined): void {
    try {
        window.sessionStorage.removeItem(ORG_SWITCH_DROP_OFF_KEY);
        if (isSafeRelativePath(path) && path !== SITE_ROOT_PATH) {
            window.sessionStorage.setItem(ORG_SWITCH_DROP_OFF_KEY, path);
        } else {
            // Root or unsafe path — nothing worth parking. Log the discard so an unexpectedly-dropped
            // origin (e.g. a malformed startURL) is observable rather than silently vanishing.
            logger.debug(`orgSwitchDropOff.persist: discarded drop-off (root/unsafe path)`);
        }
    } catch (error) {
        // Private-mode / CSP / quota errors on sessionStorage update. Non-fatal (user just returns
        // home instead of their origin) but logged so the failure is observable in prod.
        handleException(error, 'orgSwitchDropOff.persist', { level: 'warn' });
    }
}

// Reads and clears the drop-off (one-shot). Returns the path only if still safe and non-root;
// always clears so it can't replay on a later selection.
export function consumeOrgSwitchDropOff(): string | null {
    let raw: string | null = null;
    try {
        raw = window.sessionStorage.getItem(ORG_SWITCH_DROP_OFF_KEY);
        window.sessionStorage.removeItem(ORG_SWITCH_DROP_OFF_KEY);
    } catch (error) {
        // Private-mode / CSP read failure — degrade to "no drop-off" (user returns home). Logged so
        // the storage-read exception is observable rather than a silent home-redirect in prod.
        handleException(error, 'orgSwitchDropOff.consume', { level: 'warn' });
        return null;
    }
    if (isSafeRelativePath(raw) && raw !== SITE_ROOT_PATH) {
        return raw;
    }
    // A stored value that's now unsafe/root (or absent) is discarded; log when one was actually
    // present so a dropped origin doesn't vanish unobserved.
    if (raw !== null) {
        logger.debug(`orgSwitchDropOff.consume: discarded stored drop-off (root/unsafe path)`);
    }
    return null;
}

// Parks the current page and sends the user to the org picker. Shared by the "Switch org" affordance
// in ace-header (mobile card) and ace-org-context-menu so the drop-off-then-navigate sequence lives
// in one place. Uses window.location because routerStore.url may not be populated for all routes.
export function switchToOrgPicker(): void {
    // Clear the current selection so /select-org's auto-select-on-valid-cookie logic doesn't
    // immediately re-pick this same org and bounce the user straight back without ever rendering
    // the picker.
    orgContextStore.clearSelectedOrg();
    persistOrgSwitchDropOff(window.location.pathname + window.location.search);
    navigate('/' + Routes.SELECT_ORG);
}

export { ORG_SWITCH_DROP_OFF_KEY };
